import { Component, inject } from '@angular/core';
import { NxButtonModule } from '@aposin/ng-aquila/button';
import { NxHeadlineModule } from '@aposin/ng-aquila/headline';
import { NxIconModule } from '@aposin/ng-aquila/icon';
import { NX_MODAL_DATA } from '@aposin/ng-aquila/modal';
import { NxSpinnerModule } from '@aposin/ng-aquila/spinner';
import { TranslateModule } from '@ngx-translate/core';

import { NotificationsGroupComponent } from '#components/notifications-group/notifications-group.component';
import { Notification } from '#models/MicroController.model';

export type ConfirmationModalOptions =
  | {
      submit: () => Promise<void>;
      i18n: string;
      i18nOnSuccess: string;
      i18nSubmit?: string;
      bypassConfirm: false;
      notifications?: { confirm?: Notification[]; success?: Notification[] };
    }
  | {
      i18nOnSuccess: string;
      bypassConfirm: true;
    };

@Component({
  template: `<div data-testid="confirmation-modal">
    <div class="flex justify-center mb-3.5">
      <nx-icon
        style="color: var(--button-tertiary-text-color)"
        [name]="success ? 'check' : 'help-question-f'"
        outline="true"
        aria-hidden="true"
        size="m"
      />
    </div>
    <h3 class="text-center" nxHeadline size="m">
      {{ i18nByStatus | translate }}
    </h3>
    @if (notificationsByStatus; as notifications) {
      <kyc-notifications-group [notifications]="notifications" />
    }
    <div class="flex justify-center mt-7">
      <button (click)="submit()" nxButton="small-medium" type="button" [disabled]="loading">
        @if (!loading && !success) {
          {{ data.i18nSubmit ?? 'confirmation-modal.button.submit' | translate }}
        } @else if (loading) {
          <nx-spinner data-testid="submit-spinner" />
        } @else {
          {{ 'confirmation-modal.button.ok' | translate }}
        }
      </button>
    </div>
    @if (!success) {
      <div class="flex justify-center mt-3.5">
        <button (click)="data.closeModal(true)" nxButton="tertiary small-medium" type="button" [disabled]="loading">
          {{ 'confirmation-modal.button.no' | translate }}
        </button>
      </div>
    }
  </div>`,
  imports: [
    NxHeadlineModule,
    NxButtonModule,
    NxIconModule,
    NxSpinnerModule,
    TranslateModule,
    NotificationsGroupComponent,
  ],
})
export class ConfirmationModalComponent {
  readonly data = inject<{
    setShouldClose: (val: boolean) => void;
    submit?: () => Promise<void>;
    closeModal: (isDismissed?: boolean) => void;
    i18n?: string;
    i18nOnSuccess: string;
    i18nSubmit?: string;
    bypassConfirm?: boolean;
    bypassSuccess?: boolean;
    notifications?: {
      confirm?: Notification[];
      success?: Notification[];
    };
  }>(NX_MODAL_DATA);

  loading = false;
  success = false;

  constructor() {
    const data = this.data;

    if (data.bypassConfirm) {
      this.success = true;
    }
  }

  get i18nByStatus() {
    if (this.success) return this.data.i18nOnSuccess;
    return this.data.i18n ?? '';
  }

  get notificationsByStatus() {
    if (this.success) return this.data.notifications?.success;
    return this.data.notifications?.confirm;
  }

  async submit() {
    if (this.success) return this.data.closeModal();
    this.loading = true;
    this.data.setShouldClose(false);
    await this.data.submit?.();
    if (this.data.bypassSuccess) {
      this.data.setShouldClose(true);
      return this.data.closeModal();
    }
    this.success = true;
    this.loading = false;
    this.data.setShouldClose(true);
  }
}
